import { useState } from 'react'
import {
  showSuccess,
  showError,
  showValidationMessage,
  formatApiError,
} from '@/lib/utils/notifications'
import {
  setTempPassword as setUserTempPasswordApi,
  enableUser,
  disableUser,
  verifyEmail,
  resetPassword,
} from '../services/userManagementApi'
import type { UseUserManagementEditAccountActionsParams } from '../types'

export function useUserManagementEditAccountActions({
  userId,
  user,
  fetchUser,
  patchUser,
  setConfirmOpen,
  reloadNotes,
}: UseUserManagementEditAccountActionsParams) {
  const [tempPassword, setTempPassword] = useState('')
  const [showTempPwd, setShowTempPwd] = useState(false)
  const [settingTempPwd, setSettingTempPwd] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [verifyingEmail, setVerifyingEmail] = useState(false)
  const [resettingPwd, setResettingPwd] = useState(false)

  const handleSetTempPassword = async () => {
    if (!userId || !tempPassword.trim()) return
    setSettingTempPwd(true)
    try {
      const message = await setUserTempPasswordApi(userId, tempPassword.trim())
      showSuccess(message || 'Temp Password Set Successfully!')
      setTempPassword('')
      // Angular parity: reload the notes list so the backend temp-password audit note appears at once.
      reloadNotes?.()
    } catch (err) {
      const msg = formatApiError(err)
      if (typeof msg === 'string' && msg.includes('account is currently Inactive')) {
        showError(msg, true, undefined, 'Temp Password Failed')
      } else {
        showValidationMessage(msg, 'Error')
      }
    } finally {
      setSettingTempPwd(false)
      setConfirmOpen(null)
    }
  }

  const handleAccountToggle = async () => {
    if (!userId || !user) return
    setTogglingStatus(true)
    try {
      const isActive = user.account_status === 'Active'
      if (isActive) {
        await disableUser(userId)
        showSuccess('User Inactive Successfully!')
      } else {
        await enableUser(userId)
        showSuccess('User Re-Activate Successfully!')
      }
      void fetchUser()
      reloadNotes?.()
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setTogglingStatus(false)
      setConfirmOpen(null)
    }
  }

  const handleVerifyEmail = async () => {
    if (!userId) return
    setVerifyingEmail(true)
    try {
      const status = await verifyEmail(userId)
      const freshUser = await fetchUser()
      const finalStatus = status || freshUser?.email_verify || 'Verified'
      patchUser({ email_verify: finalStatus })
      showSuccess(`Email status: ${finalStatus}`)
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setVerifyingEmail(false)
    }
  }

  const handleResetPassword = async () => {
    if (!user?.email) return
    setResettingPwd(true)
    try {
      await resetPassword(user.email)
      showSuccess('Password Reset link sent successfully')
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Error')
    } finally {
      setResettingPwd(false)
      setConfirmOpen(null)
    }
  }

  return {
    tempPassword,
    setTempPassword,
    showTempPwd,
    setShowTempPwd,
    settingTempPwd,
    togglingStatus,
    verifyingEmail,
    resettingPwd,
    handleSetTempPassword,
    handleAccountToggle,
    handleVerifyEmail,
    handleResetPassword,
  }
}
